import { allWidgets } from './registry';
import { BACKEND_MODULE, GATE } from './gates';
import { isReleased } from './release';
import type {
  ModuleId, TaskRequirement, WidgetAccess, WidgetDefinition,
} from './types';

/**
 * The gate that says the reader has the module: its main endpoint's, or its backend module's top task.
 * `insights` and `links` have none: their widgets ask for their own task (notifications) or backend module (links).
 */
export const MODULE_GATES: Record<ModuleId, TaskRequirement[]> = {
  insights: [],
  'cash-position': [GATE.cashAccounts],
  investments: [GATE.investments],
  debts: [GATE.debts],
  'cash-flow': [GATE.cashFlows],
  transactions: [GATE.transactions],
  transfers: [GATE.cashPooling],
  closing: [GATE.financialClosing],
  tariffs: [GATE.tariffs],
  policies: [GATE.policies],
  reports: [GATE.reports],
  links: [],
  fx: [BACKEND_MODULE.CURRENCY_RATES],
};

/** The gates this widget needs opened: its module's and its own. */
export const widgetGates = (definition: WidgetDefinition): TaskRequirement[] =>
  [...MODULE_GATES[definition.module], ...(definition.requiresTasks ?? [])];

export const opensGate = (gate: TaskRequirement, access: WidgetAccess): boolean =>
  gate.some((taskId) => access.has(taskId));

export const allowsWidget = (definition: WidgetDefinition, access: WidgetAccess): boolean =>
  widgetGates(definition).every((gate) => opensGate(gate, access));

export const offersWidget = (definition: WidgetDefinition, access: WidgetAccess): boolean =>
  isReleased(definition) && allowsWidget(definition, access);

/** A module is offered while the reader can open at least one of its released widgets. */
export const offersModule = (module: ModuleId, access: WidgetAccess): boolean =>
  allWidgets().some((definition) => definition.module === module && offersWidget(definition, access));

/**
 * The catalogue as this reader sees it. Unknown access (still loading, or an
 * endpoint that failed) yields everything: a reader who briefly sees a widget
 * they cannot open is a smaller harm than an overview that silently loses half
 * its panels whenever `/tasks` is slow. This only decides what is *offered*:
 * the overview's slots mount no widget before the access is known
 * (`ViewPanel`'s `pending`).
 */
export const offeredWidgets = (access: WidgetAccess | undefined): WidgetDefinition[] => (
  access
    ? allWidgets().filter((definition) => offersWidget(definition, access))
    : allWidgets().filter(isReleased)
);
